import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useConfig } from "../lib/config";
import { toast } from "sonner";
import { Save, Building2, GraduationCap, Bell } from "lucide-react";

export default function Configuracion() {
  const { t } = useTranslation();
  const { config, refresh } = useConfig();
  const [form, setForm] = useState(config);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(config);
  }, [config]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/config", {
        ...form,
        alert_days: Number(form.alert_days),
        enable_unidades: Boolean(form.enable_unidades),
      });
      await refresh();
      toast.success(t("cfg.toast_saved"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("toast.error_save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-[900px] mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-12">
        <div className="mb-8 sm:mb-10">
          <div className="section-marker">{t("common.administration")}</div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter mt-2">
            {t("cfg.page_title")}<span className="serif-em text-zinc-900">.</span>
          </h1>
          <p className="mt-3 text-zinc-600 max-w-2xl">{t("cfg.desc")}</p>
        </div>

        <form onSubmit={submit} className="space-y-8 sm:space-y-10" data-testid="config-form">
          {/* Institución */}
          <section className="border border-zinc-200 p-6 sm:p-8">
            <div className="flex items-start gap-3 mb-6">
              <Building2 size={24} strokeWidth={1.5} className="text-zinc-950 mt-1" />
              <div>
                <h2 className="text-2xl font-medium tracking-tight">{t("cfg.inst_h")}</h2>
                <p className="text-sm text-zinc-500 mt-1">{t("cfg.inst_sub")}</p>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                {t("cfg.inst_nombre")}
              </label>
              <input
                required
                value={form.universidad_nombre || ""}
                onChange={set("universidad_nombre")}
                className="w-full border border-zinc-300 px-4 py-3"
                data-testid="cfg-universidad-nombre"
              />
            </div>
          </section>

          {/* Jerarquía organizacional */}
          <section className="border border-zinc-200 p-6 sm:p-8">
            <div className="flex items-start gap-3 mb-6">
              <GraduationCap size={24} strokeWidth={1.5} className="text-zinc-950 mt-1" />
              <div>
                <h2 className="text-2xl font-medium tracking-tight">
                  {t("cfg.jer_h_a")} <span className="serif-em">{t("cfg.jer_h_em")}</span>
                </h2>
                <p className="text-sm text-zinc-500 mt-1 max-w-xl">{t("cfg.jer_sub")}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="section-marker mb-3">{t("cfg.nivel1")}</div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                  {t("cfg.singular")}
                </label>
                <input
                  value={form.facultad_label_singular || ""}
                  onChange={set("facultad_label_singular")}
                  className="w-full border border-zinc-300 px-4 py-3 mb-4"
                  data-testid="cfg-fac-singular"
                />
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                  {t("cfg.plural")}
                </label>
                <input
                  value={form.facultad_label_plural || ""}
                  onChange={set("facultad_label_plural")}
                  className="w-full border border-zinc-300 px-4 py-3"
                  data-testid="cfg-fac-plural"
                />
              </div>

              <div className={form.enable_unidades ? "" : "opacity-50"}>
                <div className="section-marker mb-3 flex items-center gap-3">
                  <span>{t("cfg.nivel2")}</span>
                  <label className="inline-flex items-center gap-2 cursor-pointer normal-case">
                    <input
                      type="checkbox"
                      checked={Boolean(form.enable_unidades)}
                      onChange={(e) => setForm((f) => ({ ...f, enable_unidades: e.target.checked }))}
                      className="accent-zinc-950"
                      data-testid="cfg-enable-unidades"
                    />
                    <span className="text-xs">{form.enable_unidades ? t("cfg.activado") : t("cfg.desactivado")}</span>
                  </label>
                </div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                  {t("cfg.singular")}
                </label>
                <input
                  value={form.unidad_label_singular || ""}
                  onChange={set("unidad_label_singular")}
                  disabled={!form.enable_unidades}
                  className="w-full border border-zinc-300 px-4 py-3 mb-4 disabled:bg-zinc-100"
                  data-testid="cfg-uni-singular"
                />
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                  {t("cfg.plural")}
                </label>
                <input
                  value={form.unidad_label_plural || ""}
                  onChange={set("unidad_label_plural")}
                  disabled={!form.enable_unidades}
                  className="w-full border border-zinc-300 px-4 py-3 disabled:bg-zinc-100"
                  data-testid="cfg-uni-plural"
                />
              </div>
            </div>
          </section>

          {/* Alerta semáforo */}
          <section className="border border-zinc-200 p-6 sm:p-8">
            <div className="flex items-start gap-3 mb-6">
              <Bell size={24} strokeWidth={1.5} className="text-zinc-950 mt-1" />
              <div>
                <h2 className="text-2xl font-medium tracking-tight">
                  {t("cfg.alerta_h_a")} <span className="serif-em">{t("cfg.alerta_h_em")}</span>
                </h2>
                <p className="text-sm text-zinc-500 mt-1 max-w-xl">{t("cfg.alerta_sub")}</p>
              </div>
            </div>
            <div className="flex items-end gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                  {t("cfg.dias")}
                </label>
                <input
                  type="number"
                  min="1"
                  max="730"
                  value={form.alert_days || 90}
                  onChange={set("alert_days")}
                  className="border border-zinc-300 px-4 py-3 w-32 tabular-nums text-center text-2xl kpi-number"
                  data-testid="cfg-alert-days"
                />
              </div>
              <div className="pb-4 text-sm text-zinc-500">
                ≈ {Math.round((form.alert_days || 90) / 30 * 10) / 10} {t("cfg.months")}
              </div>
            </div>
          </section>

          <div className="flex justify-end gap-3">
            <button
              type="submit"
              disabled={saving}
              className="bg-zinc-950 text-white px-6 py-3 font-medium hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="cfg-submit"
            >
              <Save size={18} strokeWidth={1.5} />
              {saving ? t("cfg.saving") : t("cfg.save")}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
